import { bindingFor, type TemplateVariableSource } from "@orbis/shared";
import { type FormEvent, useMemo, useState } from "react";
import { useRole } from "../../components/AppShell";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { fillTemplate, formatPhone, TEMPLATE_CATEGORY_LABEL } from "../../lib/format";
import {
  useCompany,
  useConversation,
  useMe,
  useOrganizations,
  useSendTemplate,
  useSyncTemplates,
  useTemplates,
} from "../../lib/queries";
import type { Template } from "../../lib/types";

/** Picks an approved WhatsApp template, fills its variables and sends it. */
export function TemplatePicker({ conversationId, onDone }: { conversationId: string; onDone?: () => void }) {
  const templates = useTemplates(true);
  const send = useSendTemplate(conversationId);
  const sync = useSyncTemplates();
  const role = useRole();
  const toast = useToast();
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState<Record<string, string>>({});
  const [header, setHeader] = useState<Record<string, string>>({});
  const conv = useConversation(conversationId);
  const me = useMe();
  const company = useCompany();
  const organizations = useOrganizations();
  const contact = conv.data?.contact;

  // Values Orbis already knows, for variables bound to them (or named {{nombre}}, {{empresa}}…).
  const known: Record<TemplateVariableSource, string> = {
    contact_name: contact?.name ?? "",
    contact_phone: contact ? formatPhone(contact.waId) : "",
    contact_email: contact?.email ?? "",
    contact_organization: organizations.data?.find((o) => o.id === contact?.organizationId)?.name ?? "",
    agent_name: me.data?.user.name ?? "",
    company_name:
      me.data?.tenants.find((x) => x.tenantId === me.data?.activeTenantId)?.tenantName ??
      me.data?.tenants[0]?.tenantName ??
      "",
    company_phone: company.data?.phone ?? "",
    company_address: company.data?.address ?? "",
    company_hours: company.data?.hours ?? "",
    company_website: company.data?.website ?? "",
    today: new Intl.DateTimeFormat("es-CR", { day: "numeric", month: "long", timeZone: "America/Costa_Rica" }).format(
      new Date(),
    ),
  };
  function prefill(next: Template, scope: "body" | "header") {
    const names = scope === "body" ? next.bodyVariables : next.headerVariables;
    const values: Record<string, string> = {};
    for (const name of names) {
      const b = bindingFor(next.variableMap ?? {}, scope, name);
      if (!b) continue;
      const value = "fixed" in b ? b.fixed : known[b.source];
      if (value) values[name] = scope === "header" ? value.slice(0, 60) : value;
    }
    return values;
  }

  const t = useMemo(() => templates.data?.find((x) => x.id === templateId), [templates.data, templateId]);
  const preview = t
    ? [t.headerText ? fillTemplate(t.headerText, header) : null, fillTemplate(t.bodyText, body), t.footerText]
        .filter(Boolean)
        .join("\n\n")
    : "";
  const complete =
    !!t && t.bodyVariables.every((v) => body[v]?.trim()) && t.headerVariables.every((v) => header[v]?.trim());

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!t || !complete) return;
    try {
      const sent = await send.mutateAsync({ templateId: t.id, bodyValues: body, headerValues: header });
      if (sent.deliveryStatus === "failed") toast("WhatsApp no aceptó la plantilla. Revisa el número o el token.");
      setTemplateId("");
      setBody({});
      setHeader({});
      onDone?.();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo enviar la plantilla.");
    }
  }

  if (templates.data?.length === 0) {
    return (
      <div className="template-picker">
        <p className="muted">
          No hay plantillas aprobadas. Créalas en el administrador de WhatsApp de Meta y luego sincronízalas.
        </p>
        {(role === "owner" || role === "admin") && (
          <button
            className="btn btn-sm"
            type="button"
            disabled={sync.isPending}
            onClick={() => sync.mutateAsync().then(() => toast("Plantillas sincronizadas"))}
          >
            {sync.isPending ? "Sincronizando…" : "Sincronizar plantillas"}
          </button>
        )}
      </div>
    );
  }

  const field = (scope: "header" | "body", name: string, values: Record<string, string>, set: typeof setBody) => (
    <div className="field" key={`${scope}-${name}`}>
      <label htmlFor={`tpl-${scope}-${name}`}>
        {scope === "header" ? "Encabezado" : "Variable"} {`{{${name}}}`}
        {t && bindingFor(t.variableMap ?? {}, scope, name) && <span className="faint"> · automática</span>}
      </label>
      <input
        className="input"
        id={`tpl-${scope}-${name}`}
        placeholder={`Lo que va en {{${name}}}`}
        value={values[name] ?? ""}
        maxLength={scope === "header" ? 60 : 1024}
        onChange={(e) => set({ ...values, [name]: e.target.value })}
      />
    </div>
  );

  return (
    <form className="template-picker" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="tpl-select">Plantilla aprobada</label>
        <select
          className="select"
          id="tpl-select"
          value={templateId}
          onChange={(e) => {
            setTemplateId(e.target.value);
            const next = templates.data?.find((x) => x.id === e.target.value);
            setBody(next ? prefill(next, "body") : {});
            setHeader(next ? prefill(next, "header") : {});
          }}
        >
          <option value="">Elige una plantilla…</option>
          {templates.data?.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name} · {x.language} · {TEMPLATE_CATEGORY_LABEL[x.category] ?? x.category}
            </option>
          ))}
        </select>
      </div>
      {t && (
        <>
          <div className="template-fields">
            {t.headerVariables.map((v) => field("header", v, header, setHeader))}
            {t.bodyVariables.map((v) => field("body", v, body, setBody))}
          </div>
          <div className="field">
            <span className="faint">Así lo verá el cliente</span>
            <div className="bubble out human template-preview">{preview}</div>
          </div>
          {t.category === "MARKETING" && (
            <small className="faint">
              Meta cobra las plantillas de marketing a una tarifa mayor que las de utilidad.
            </small>
          )}
          <div className="row" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {onDone && (
              <button className="btn btn-sm" type="button" onClick={onDone}>
                Cancelar
              </button>
            )}
            <button className="btn btn-primary btn-sm" type="submit" disabled={!complete || send.isPending}>
              {send.isPending ? "Enviando…" : "Enviar plantilla"}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
